import type { ReactNode } from "react";
import React, { createContext, useContext, useEffect } from "react";

import type {
  DispatchActionFn,
  PageState,
  RegisterPageCallback,
} from "./PageStateProvider";
import {
  ActionsContext,
  RegisterPageContext,
  useInternalPageState,
} from "./PageStateProvider";

export const PageStateContext = createContext<{
  state?: string;
  delay?: number;
  params?: Record<string, string>;
}>({ state: "", params: {} });

interface UseRegisterPageEffectProps {
  path: string;
  delay?: number;
}

interface PageProps {
  path: string;
  delay?: number;
  children: ReactNode;
}

const useRegisterPageEffect = ({ path, delay }: UseRegisterPageEffectProps) => {
  const registerPage = useContext(RegisterPageContext) as RegisterPageCallback;
  useEffect(() => {
    registerPage({
      page: path,
      delay: delay ?? 0,
    });
  }, [delay, registerPage, path]);
};

const useDelayedClosingEffect = ({ path }: { path: string }) => {
  const pageState = useInternalPageState() as PageState;
  const dispatchAction = useContext(ActionsContext) as DispatchActionFn;
  useEffect(() => {
    const page = pageState[path];
    if (page?.state === "closing") {
      const timer = setTimeout(
        () =>
          dispatchAction({
            type: "close",
            targetPage: path,
          }),
        page?.delay,
      );
      return () => clearTimeout(timer);
    }
  }, [pageState, dispatchAction, path]);
};

export const Page = ({ children, path, delay }: PageProps) => {
  const pageState = useInternalPageState() as PageState;
  useRegisterPageEffect({ path, delay });
  useDelayedClosingEffect({ path });

  const page = pageState[path];
  return page ? (
    <PageStateContext.Provider value={page}>
      {children}
    </PageStateContext.Provider>
  ) : null;
};

export const usePageState = () => {
  const { state } = useContext(PageStateContext);
  return { isOpen: state === "open" };
};
